import * as React from "react";
import { request } from "../../services/cpln";
import { useAppSelector } from "../../store/hooks";
import { selectOrg } from "../../store/contextSlice";

interface LabelValuesResponse {
  status: string;
  data: string[];
}

interface MetricsLabelValues {
  gvcs: string[];
  workloads: string[];
  locations: string[];
  containers: string[];
  isLoading: boolean;
}

export function useMetricsLabelValues(): MetricsLabelValues {
  const org = useAppSelector(selectOrg);
  const [gvcs, setGvcs] = React.useState<string[]>([]);
  const [workloads, setWorkloads] = React.useState<string[]>([]);
  const [locations, setLocations] = React.useState<string[]>([]);
  const [containers, setContainers] = React.useState<string[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    if (!org) return;

    let cancelled = false;

    async function fetchAll() {
      setIsLoading(true);
      try {
        const labels = ["gvc", "workload", "location", "container_name"] as const;
        const results = await Promise.all(
          labels.map((label) =>
            request<LabelValuesResponse>({
              service: "metrics",
              method: "get",
              url: `/metrics/org/${org}/api/v1/label/${label}/values`,
            }).then((res) => res.data.data?.sort() ?? []),
          ),
        );
        if (!cancelled) {
          setGvcs(results[0]);
          setWorkloads(results[1]);
          setLocations(results[2]);
          setContainers(results[3]);
        }
      } catch (e) {
        console.error("Failed to fetch metrics label values", e);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchAll();

    return () => {
      cancelled = true;
    };
  }, [org]);

  return { gvcs, workloads, locations, containers, isLoading };
}
